import React, { Component } from 'react';
import { Button, Confirm } from 'semantic-ui-react';
import {
  castArray,
  cloneDeep,
  concat,
  fill,
  filter,
  find,
  flattenDeep,
  head,
  isArray,
  isEmpty,
  isNil,
  map,
  some
} from 'lodash';
import { connect } from 'react-redux';
import OccurrenceRow from '../components/experimentalDesign/OccurrenceRow';
import Condition from '../components/experimentalDesign/Condition';
import {
  getProjectConditions,
  setActiveProjectLocking,
  setActiveProjectSection,
  SINGLE_PROJECT_EXPERIMENTAL_SECTION
} from '../data/projectActions';
import {
  EventForm,
  OccurrencesList,
  SignalForm,
  TransformationForm,
  StackList,
  StimuliForm,
  UpdateDataAcquisitionSignalForm
} from '../components/experimentalDesign/modals';
import {
  createConditionInProject,
  deleteCondition,
  invalidateConditions
} from '../data/experimentalDesign/conditionActions';

import OccurrenceManipulator from '../components/experimentalDesign/OccurrenceManipulator';
import { closeAllModals } from '../data/experimentalDesign/modalActions';
import {
  createEventForProject,
  createTransformationForProject,
  createSignalForProject,
  createStimuliForProject,
  deleteEvent,
  deleteTransformation,
  deleteSignal,
  deleteStimuli,
  getEventsForProject,
  getSignalsForProject,
  getTransformationsForProject,
  getSelectedTransformationsForProject,
  getStimuliForProject,
  invalidateOccurrences,
  updateEventForProject,
  updateTransformationForProject,
  updateSignalForProject,
  updateStimuliForProject
} from '../data/experimentalDesign/occurrenceActions';
import {
  createOccurrenceLocation,
  deleteOccurrenceLocation,
  updateOccurrenceLocation
} from '../data/experimentalDesign/occurrenceLocationAction';
import { getSignalTypes, getTransformations } from '../data/experimentalDesign/staticDataActions';
import DataAcquisitionUpdateForm from '../components/experimentalDesign/modals/DataAcquisitionUpdateForm';
import occurrencesCorrespondenceTable from '../data/occurencesCorrespondenceTable';

/**
 * Basic empty condition
 * @type {{name: string, participants: number, occurrences: *[]}}
 */
const emptyCondition = {
  name: 'Condition 1',
  nbr_participations: 12,
  occurrences: [fill(Array(50), null), fill(Array(50), null)]
};

/**
 * Utility function to add the type
 * @param type
 * @param list
 * @returns {*}
 */
const addTypeToOccurrence = (type, list) => {
  return map(list, (el, i) => {
    return { ...el, type };
  });
};

/**
 * Temporary function: When receiving new props (the project) it keeps the active condition occurrences and blends it with the new props project.
 * @param conditions
 * @param previousConditions
 * @returns {*}
 */
const addOccurrencesToCondition = (conditions, previousConditions = null) => {
  return map(conditions, (condition, i) => {
    if (!('occurrences' in condition)) {
      if (!isNil(previousConditions)) {
        // Find matching condition in prevState
        const matchingCondition = find(previousConditions, { id: condition.id });
        condition = cloneDeep(condition);
        if (matchingCondition !== undefined) {
          condition.occurrences = cloneDeep(matchingCondition.occurrences);
        } else {
          condition.occurrences = cloneDeep(emptyCondition.occurrences);
        }
      } else {
        condition.occurrences = cloneDeep(emptyCondition.occurrences);
      }
    }

    return condition;
  });
};

const unlockProjectPrompt = (props, cancelCallback) => {
  if (
    window.confirm(
      'Experimental Design is locked ! Unlocking the project will wipe all session information and data. Are you sure you want to proceed?'
    )
  ) {
    props.dispatch(setActiveProjectLocking(props.project, false));
  } else {
    cancelCallback && cancelCallback();
  }
};

class ExperimentalDesign extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hasProject: false,
      occurrences: {
        signals: null,
        events: null,
        stimuli: null,
        transformations: null,
        selectedTransformations: null
      },
      conditions: null
    };

    /**
     * Mapping of edit forms for each occurrence type
     * @type {{signal: SignalForm, event: EventForm, stimuli: StimuliForm}}
     */
    this.occurrencesFormComponents = {
      signal: {
        form: SignalForm,
        editSubmit: updateSignalForProject,
        createSubmit: createSignalForProject
      },
      transformation: {
        form: TransformationForm,
        editSubmit: updateTransformationForProject,
        createSubmit: createTransformationForProject
      },
      event: {
        form: EventForm,
        editSubmit: updateEventForProject,
        createSubmit: createEventForProject
      },
      stimuli: {
        form: StimuliForm,
        editSubmit: updateStimuliForProject,
        createSubmit: createStimuliForProject
      }
    };

    this.occurrenceDeleteHandlers = {
      signal: (id, projectId, versionId, callback) => {
        this.props.dispatch(deleteSignal(id, projectId, versionId, callback));
      },
      selectedTransformations: (id, projectId, callback) => {
        this.props.dispatch(deleteTransformation(id, projectId, callback));
      },
      event: (id, projectId, callback) => {
        this.props.dispatch(deleteEvent(id, projectId, callback));
      },
      stimuli: (id, projectId, callback) => {
        this.props.dispatch(deleteStimuli(id, projectId, callback));
      }
    };

    this.closeAllModals = this.closeAllModals.bind(this);

    this.lockProject = this.lockProject.bind(this);
    this.unlockProject = this.unlockProject.bind(this);
    this.addCondition = this.addCondition.bind(this);
    this.deleteCondition = this.deleteCondition.bind(this);
    this.setOccurrenceInCondition = this.setOccurrenceInCondition.bind(this);

    this.conditionOccurrenceDropHandler = this.conditionOccurrenceDropHandler.bind(this);

    this.removeOccurrenceFromProject = this.removeOccurrenceFromProject.bind(this);
    this.removeOccurrenceFromCondition = this.removeOccurrenceFromCondition.bind(this);
    this.removeOccurrenceFromOccurrenceStack = this.removeOccurrenceFromOccurrenceStack.bind(this);
    this.removeOccurrenceFromOccurrenceStackHandler = this.removeOccurrenceFromOccurrenceStackHandler.bind(
      this
    );
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (isNil(nextProps.project)) {
      return null;
    }
    if (!prevState.hasProject && !isNil(nextProps.project)) {
      nextProps.dispatch(getSignalsForProject(nextProps.project.id));
      nextProps.dispatch(getSelectedTransformationsForProject(nextProps.project.id));
      nextProps.dispatch(getTransformationsForProject(nextProps.project.id));
      nextProps.dispatch(getEventsForProject(nextProps.project.id));
      nextProps.dispatch(getStimuliForProject(nextProps.project.id));
      nextProps.dispatch(getProjectConditions(nextProps.project.id));
    }

    const prevConditions = prevState.conditions || null;
    return {
      hasProject: !isNil(nextProps.project),
      occurrences: {
        signals: addTypeToOccurrence('signal', nextProps.signals),
        events: addTypeToOccurrence('event', nextProps.events),
        stimuli: addTypeToOccurrence('stimuli', nextProps.stimuli),
        transformations: addTypeToOccurrence('transformation', nextProps.transformations),
        selectedTransformations: addTypeToOccurrence(
          'selectedTransformations',
          nextProps.selectedTransformations
        )
      },
      conditions: addOccurrencesToCondition(nextProps.conditions, prevConditions)
    };
  }

  componentDidMount() {
    this.props.dispatch(setActiveProjectSection(SINGLE_PROJECT_EXPERIMENTAL_SECTION));
    this.props.dispatch(getSignalTypes());
    // this.props.dispatch(getTransformations());
  }

  /**
   * Remove Data on Unmount
   */
  componentWillUnmount() {
    this.props.dispatch(invalidateOccurrences());
    this.props.dispatch(invalidateConditions());
  }

  /**
   * Closes all modals
   */
  closeAllModals() {
    this.props.dispatch(closeAllModals());
  }

  lockProject() {
    this.props.history.push(`/projects/${this.props.project.id}/file-import`);
    this.props.dispatch(setActiveProjectLocking(this.props.project, true));
  }

  unlockProject() {
    unlockProjectPrompt(this.props);
  }

  /**
   * Adds an empty condition to the state
   */
  addCondition() {
    this.props.dispatch(
      createConditionInProject(
        {
          ...emptyCondition,
          name: `Condition ${this.state.conditions.length + 1}`
        },
        this.props.project.id
      )
    );
  }

  /**
   *  Deletes the condition indicated in the delete condition modal state. It is this way because of the confirm callback for the confirm modal.
   */
  deleteCondition() {
    this.props.dispatch(
      deleteCondition(this.props.modals.condition.delete, () => {
        this.closeAllModals();
      })
    );
  }

  /**
   * Occurrences
   */

  /**
   * Compares two occurrences
   * @param occurrence1
   * @param occurrence2
   */
  areOccurrencesInSlotSameType(occurrence1, occurrence2) {
    const getTypeFromOccurrence = occurrence => {
      // If stack, get first element of array and get type.

      return isNil(occurrence)
        ? null
        : isArray(occurrence)
        ? head(occurrence).type
        : occurrence.type;
    };

    return getTypeFromOccurrence(occurrence1) === getTypeFromOccurrence(occurrence2);
  }

  /**
   * Sets an occurrence in a condition, in a given occurrence slot.
   * @param isNew
   * @param occurrence
   * @param toConditionIndex
   * @param toOccurrenceRow
   * @param toOccurrenceIndex
   * @param fromConditionIndex
   * @param fromOccurrenceRow
   * @param fromOccurrenceIndex
   * @returns {{conditions: *}}
   */
  setOccurrenceInCondition(
    isNew = false,
    occurrence,
    toConditionIndex,
    toOccurrenceRow,
    toOccurrenceIndex,
    fromConditionIndex,
    fromOccurrenceRow,
    fromOccurrenceIndex
  ) {
    let { conditions } = this.state;
    let occurrenceToMove = cloneDeep(occurrence);
    /**
     * Is the occurrence being dragged from a condition ?
     * @type {boolean}
     */
    const isFromCondition = !isNew && (!isNil(occurrence) || !isNil(fromConditionIndex));
    /**
     * Is the target and the source of the same type ?
     */
    const ofSameType = this.areOccurrencesInSlotSameType(
      isFromCondition
        ? conditions[fromConditionIndex].occurrences[fromOccurrenceRow][fromOccurrenceIndex]
        : occurrenceToMove,
      conditions[toConditionIndex].occurrences[toOccurrenceRow][toOccurrenceIndex]
    );
    /**
     * Only occurrences of the same type can be merged. This condition is valid only if target occurrence slot is not empty.
     */
    if (
      !ofSameType &&
      !isNil(conditions[toConditionIndex].occurrences[toOccurrenceRow][toOccurrenceIndex])
    ) {
      return this.state.conditions;
    }

    if (isFromCondition) {
      conditions = map(conditions, (el, i) => {
        // extract occurrence
        if (fromConditionIndex === i) {
          el = cloneDeep(el);
          el.occurrences = el.occurrences.slice();
          occurrenceToMove = cloneDeep(el.occurrences[fromOccurrenceRow][fromOccurrenceIndex]);
          el.occurrences[fromOccurrenceRow][fromOccurrenceIndex] = null;
        }
        return el;
      });

      /**
       * Update the existant occurrenceLocation
       */

      this.props.dispatch(
        updateOccurrenceLocation(
          occurrenceToMove,
          toOccurrenceRow,
          toOccurrenceIndex,
          toConditionIndex
        )
      );
    } else {
      /**
       * Create a new occurrenceLocation
       */

      this.props.dispatch(
        createOccurrenceLocation(
          occurrenceToMove,
          toOccurrenceRow,
          toOccurrenceIndex,
          toConditionIndex
        )
      );
    }

    return {
      conditions: map(conditions, (el, i) => {
        // set occurrence
        if (toConditionIndex === i) {
          el = cloneDeep(el);
          el.occurrences = el.occurrences.slice();

          if (
            isNil(el.occurrences[toOccurrenceRow][toOccurrenceIndex]) ||
            isNil(occurrenceToMove)
          ) {
            el.occurrences[toOccurrenceRow][toOccurrenceIndex] = occurrenceToMove;
          } else {
            el.occurrences[toOccurrenceRow][toOccurrenceIndex] = flattenDeep(
              concat(
                castArray(el.occurrences[toOccurrenceRow][toOccurrenceIndex]),
                occurrenceToMove
              )
            );
          }
        }
        return el;
      })
    };
  }

  /**
   * Removes an occurrence from a condition.
   * @param conditionIndex
   * @param occurrenceRow
   * @param occurrenceIndex
   * @returns {{conditions: T[]}}
   */
  removeOccurrenceFromConditionHandler(conditionIndex, occurrenceRow, occurrenceIndex) {
    return {
      conditions: map(this.state.conditions, (el, i) => {
        // set occurrence
        if (conditionIndex === i) {
          el = cloneDeep(el);
          el.occurrences = el.occurrences.slice();

          this.props.dispatch(
            deleteOccurrenceLocation(el.occurrences[occurrenceRow][occurrenceIndex])
          );

          el.occurrences[occurrenceRow][occurrenceIndex] = null;
        }
        return el;
      })
    };
  }

  /**
   * Square drop handler
   * @param event
   * @param conditionIndex
   * @param occurrenceRow
   * @param occurrenceIndex
   */
  conditionOccurrenceDropHandler(event, conditionIndex, occurrenceRow, occurrenceIndex) {
    const fromConditionIndex = parseInt(event.dataTransfer.getData('fromConditionIndex'), 10);
    const fromOccurrenceRow = parseInt(event.dataTransfer.getData('fromOccurrenceRow'), 10);
    const fromOccurrenceIndex = parseInt(event.dataTransfer.getData('fromOccurrenceIndex'), 10);
    const isNew = event.dataTransfer.getData('isNew') === 'true';
    let occurrence = event.dataTransfer.getData('occurrence');

    occurrence = isEmpty(occurrence) ? null : JSON.parse(occurrence);

    this.setState(() =>
      this.setOccurrenceInCondition(
        isNew,
        occurrence,
        conditionIndex,
        occurrenceRow,
        occurrenceIndex,
        fromConditionIndex,
        fromOccurrenceRow,
        fromOccurrenceIndex
      )
    );
  }

  /**
   * Removes the occurrence set from the delete occurrence modal. Removes it from stacks too.
   */
  removeOccurrenceFromProject() {
    const conditions = map(this.state.conditions, el => {
      const condition = cloneDeep(el);
      // Map Rows
      condition.occurrences = map(condition.occurrences, occurrenceRow => {
        // Filter occurrences in row, remove ones matching the one to delete. Then, map what's left and do the same process inside stacks.
        return map(
          map(occurrenceRow, occurrenceInRow => {
            if (
              isNil(occurrenceInRow) ||
              (occurrenceInRow.type === this.props.modals.globals.delete.source &&
                occurrenceInRow.id === this.props.modals.globals.delete.id)
            ) {
              return null;
            }
            return occurrenceInRow;
          }),
          occurrenceInRow => {
            if (isArray(occurrenceInRow)) {
              const stack = filter(occurrenceInRow, occurrenceInStack => {
                return !(
                  occurrenceInStack.type === this.props.modals.globals.delete.source &&
                  occurrenceInStack.id === this.props.modals.globals.delete.id
                );
              });

              if (isEmpty(stack)) {
                return null;
              }

              if (stack.length === 1) {
                return stack.shift();
              }

              return stack;
            }
            return occurrenceInRow;
          }
        );
      });

      return condition;
    });

    const deleteFunction = this.occurrenceDeleteHandlers[this.props.modals.globals.delete.source];
    if (this.props.modals.globals.delete.source === 'signal') {
      deleteFunction(
        this.props.modals.globals.delete.id,
        this.props.project.id,
        this.props.modals.globals.delete.versionId,
        () => {
          this.setState(
            {
              conditions
            },
            () => {
              this.closeAllModals();
            }
          );
        }
      );
    } else if (this.props.modals.globals.delete.source === 'selectedTransformations') {
      deleteFunction(this.props.modals.globals.delete.id, this.props.project.id, () => {
        this.setState(
          {
            conditions
          },
          () => {
            this.closeAllModals();
          }
        );
      });
    } else {
      deleteFunction(this.props.modals.globals.delete.id, this.props.project.id, () => {
        this.setState(
          {
            conditions
          },
          () => {
            this.closeAllModals();
          }
        );
      });
    }
  }

  /**
   * Removes an occurrence from a stack of occurrences. Function called by the state modifier.
   * @param conditionIndex
   * @param stackRow
   * @param stackIndex
   * @param occurrenceIndex
   * @returns {{conditions: T[]}}
   */
  removeOccurrenceFromOccurrenceStackHandler(
    conditionIndex,
    stackRow,
    stackIndex,
    occurrenceIndex
  ) {
    const { conditions } = this.state;
    return {
      conditions: map(conditions, (el, i) => {
        let condition = el;
        if (conditionIndex === i) {
          condition = cloneDeep(el);
          if (isArray(condition.occurrences[stackRow][stackIndex])) {
            condition.occurrences[stackRow][stackIndex] = condition.occurrences[stackRow][
              stackIndex
            ].filter((occurrence, k) => {
              return k === occurrenceIndex;
            });

            // If he's last, shift him on top and close modal.
            if (condition.occurrences[stackRow][stackIndex].length === 1) {
              condition.occurrences[stackRow][stackIndex] = condition.occurrences[stackRow][
                stackIndex
              ].shift();
              this.closeAllModals();
            }
            this.props.dispatch(
              deleteOccurrenceLocation(condition.occurrences[stackRow][stackIndex])
            );
          }
        }
        return condition;
      })
    };
  }

  /**
   * Removes an occurrence from a given slot
   * @param event
   * @param conditionIndex
   * @param occurrenceRow
   * @param occurrenceIndex
   */
  removeOccurrenceFromCondition(event, conditionIndex, occurrenceRow, occurrenceIndex) {
    if (event.dataTransfer.dropEffect === 'none') {
      this.setState(() =>
        this.removeOccurrenceFromConditionHandler(conditionIndex, occurrenceRow, occurrenceIndex)
      );
    }
  }

  /**
   * Removes an occurrence for an occurrence stack from a given slot
   * @param event
   * @param conditionIndex
   * @param stackRow
   * @param stackIndex
   * @param occurrenceIndex
   */
  removeOccurrenceFromOccurrenceStack(
    event,
    conditionIndex,
    stackRow,
    stackIndex,
    occurrenceIndex
  ) {
    if (event.dataTransfer.dropEffect === 'none') {
      const conditions = this.removeOccurrenceFromOccurrenceStackHandler(
        conditionIndex,
        stackRow,
        stackIndex,
        occurrenceIndex
      );
      this.setState(() => conditions);
    }
  }

  /**
   * Gets the confirm modal message. Done this way because the condition might be deleted before the confirm is closed.
   * @returns {*}
   */
  getConditionDeletionMessage() {
    if (some(this.state.conditions, { id: this.props.modals.condition.delete })) {
      const condition = find(this.state.conditions, { id: this.props.modals.condition.delete });
      return (
        <div style={{ padding: '10px' }}>
          Delete {condition.name}?
          <br />
          <br />
          Warning, all of your elements inside the condition will disappear but will stay inside the
          global list.
        </div>
      );
    }
    return null;
  }

  render() {
    /**
     * List of supported occurrences
     * @type {string[]}
     */
    const occurrencesTypes = ['signal', 'event', 'stimuli', 'transformation'];

    /**
     * Dynamic Components
     */

    /**
     * Top row Occurrence Menu
     * @type {any[]}
     */
    const occurrencesManipulators = map(occurrencesTypes, (type, i) => {
      return (
        <OccurrenceManipulator
          key={i}
          occurrenceType={type}
          occurrenceName={occurrencesCorrespondenceTable[type]}
        />
      );
    });

    /**
     * List of conditions for the bottom content
     */
    const conditions = map(this.state.conditions, (condition, i) => {
      return (
        <Condition
          key={i}
          condition={condition}
          index={i}
          occurrenceDropHandler={this.conditionOccurrenceDropHandler}
          occurrenceDragEndHandler={this.removeOccurrenceFromCondition}
        />
      );
    });

    const occurrenceListCols = {
      signal: ['id', 'name', 'equipment_id', 'hardware_id', 'software_id', 'version_id'],
      event: ['id', 'name', 'software_id', 'version_id'],
      stimuli: ['id', 'name', 'width', 'height', 'software_id', 'version_id'],
      transformation: ['id', 'name']
    };

    /**
     * Array of occurrence listing modals
     * @type {any[]}
     */
    const occurrencesListModals = map(occurrencesTypes, (el, i) => {
      return (
        <OccurrencesList
          key={i}
          customStyle={{ maxWidth: '80%', maxHeight: '80%' }}
          occurrences={this.state.occurrences[occurrencesCorrespondenceTable[el]]}
          isOpen={this.props.modals[occurrencesCorrespondenceTable[el]].list}
          columns={occurrenceListCols[el]}
          onRequestClose={this.closeAllModals}
        />
      );
    });

    /**
     * Array of occurrence edit modals
     * @type {any[]}
     */
    const occurrencesEditModals = map(occurrencesTypes, (el, i) => {
      const occurrenceValues = this.props.modals[occurrencesCorrespondenceTable[el]].edit;
      const props = {
        key: i,
        initialValues: occurrenceValues !== false ? occurrenceValues : undefined,
        isOpen: this.props.modals[occurrencesCorrespondenceTable[el]].edit !== false,
        onRequestClose: this.closeAllModals,
        type: el,
        onSubmit: value => {
          this.props.dispatch(
            this.occurrencesFormComponents[el].editSubmit(
              value,
              this.props.project.id,
              this.closeAllModals
            )
          );
        }
      };

      if (el === 'signal') {
        return <UpdateDataAcquisitionSignalForm {...props} />;
      }
      return <DataAcquisitionUpdateForm {...props} />;
    });

    /**
     * Array of occurrence create modals
     * @type {any[]}
     */
    const occurrencesCreateModals = map(occurrencesTypes, (el, i) => {
      const CreateForm = this.occurrencesFormComponents[el].form;
      const props = {
        key: i,
        initialValues: { time: +new Date() },
        isOpen: this.props.modals[occurrencesCorrespondenceTable[el]].add !== false,
        onRequestClose: this.closeAllModals,
        onSubmit: value => {
          this.props.dispatch(
            this.occurrencesFormComponents[el].createSubmit(
              value,
              this.props.project.id,
              this.closeAllModals
            )
          );
        }
      };

      return <CreateForm {...props} />;
    });

    /**
     * Deletion message for the condition Confirm screen
     */
    const deleteConditionMessage = this.getConditionDeletionMessage();

    /**
     * Deletion message for an occurrence
     */
    let deleteGlobalOccurrenceHeader = null;
    let deleteGlobalOccurrenceMessage = null;

    if (this.props.modals.globals.delete) {
      let secondaryMessage = null;
      deleteGlobalOccurrenceHeader = `Removing ${this.props.modals.globals.delete.source}`;
      if (this.props.modals.globals.delete.source === 'selectedTransformations') {
        deleteGlobalOccurrenceHeader = `Removing transformation`;
        secondaryMessage = (
          <span>
            <br />
            <br /> Warning, this will delete the transformation from the experimental design.
          </span>
        );
      } else if (this.props.modals.globals.delete.source !== 'signal') {
        secondaryMessage = (
          <span>
            <br />
            <br /> Warning, this {this.props.modals.globals.delete.source} will be removed from
            every condition.
          </span>
        );
      }
      deleteGlobalOccurrenceMessage = (
        <div style={{ padding: '1.5rem' }}>
          Are you sure you want to remove {this.props.modals.globals.delete.source} -{' '}
          {this.props.modals.globals.delete.name}?{secondaryMessage}
        </div>
      );
      if (this.props.modals.globals.delete.source === 'selectedTransformations') {
        deleteGlobalOccurrenceMessage = (
          <div style={{ padding: '1.5rem' }}>
            Are you sure you want to remove the transformation -{' '}
            {this.props.modals.globals.delete.name}?{secondaryMessage}
          </div>
        );
      }
    }

    let lockProjectButton = null;
    if (this.props.project) {
      if (this.props.project.template_locked === 0) {
        lockProjectButton = (
          <Button id="lockProject" color="green" onClick={this.lockProject}>
            <span className="icon-Unlock" /> Unlocked
          </Button>
        );
      } else {
        lockProjectButton = (
          <Button id="lockProject" color="red" onClick={this.unlockProject}>
            <span className="icon-lock2" /> Locked
          </Button>
        );
      }
    }
    return (
      <div className="dashboard-global">
        {this.props.project && this.props.project.template_locked === 1 && (
          <div className="experimentalDesignBlock" />
        )}
        <h2 className="dashboard-global__main-title">Experimental Design</h2>

        {lockProjectButton}

        <div className="square-tools">{occurrencesManipulators}</div>

        <h5 className="dashboard-global__sub-title">Signals</h5>

        <div className="grid-pair grid-pair--main">
          <OccurrenceRow
            occurrences={concat(
              this.state.occurrences.signals,
              this.state.occurrences.selectedTransformations
            )}
            draggable={false}
          />

          <h5 className="dashboard-global__sub-title">Events and Stimuli</h5>

          <OccurrenceRow
            occurrences={concat(this.state.occurrences.events, this.state.occurrences.stimuli)}
            draggable
          />
        </div>
        <p className="dashboard-global__paragraph">
          Drag and drop below to simulate different timelines of conditions
        </p>

        <div className="dashboard-global__container">
          <h4>Condtions</h4>
          <div className="dashboard-global__btn-wrapper">
            <Button onClick={this.addCondition}>Add Condition</Button>
          </div>
          <div className="dashboard-global__block">{conditions}</div>
        </div>

        <Confirm
          open={this.props.modals.condition.delete !== false}
          content={deleteConditionMessage}
          onCancel={this.closeAllModals}
          onConfirm={this.deleteCondition}
        />

        <Confirm
          open={this.props.modals.globals.delete !== false}
          header={deleteGlobalOccurrenceHeader}
          content={deleteGlobalOccurrenceMessage}
          onCancel={this.closeAllModals}
          onConfirm={this.removeOccurrenceFromProject}
        />

        <StackList
          conditions={this.state.conditions}
          isOpen={this.props.modals.condition.stack !== false}
          dragOccurrenceHandler={this.removeOccurrenceFromOccurrenceStack}
          onRequestClose={this.closeAllModals}
          {...this.props.modals.condition.stack}
        />

        {occurrencesCreateModals}

        {occurrencesListModals}

        {occurrencesEditModals}
      </div>
    );
  }
}

export default connect(state => ({
  project: state.projects.single.active,
  modals: state.projects.single.experimentalDesign.modals,
  conditions: state.projects.single.conditions,
  signals: state.projects.single.experimentalDesign.signals,
  transformations: state.projects.single.experimentalDesign.transformations,
  selectedTransformations: state.projects.single.experimentalDesign.selectedTransformations,
  events: state.projects.single.experimentalDesign.events,
  stimuli: state.projects.single.experimentalDesign.stimuli
}))(ExperimentalDesign);



// WEBPACK FOOTER //
// ./src/scenes/projects/scenes/singleProject/containers/ExperimentalDesign.js